// ─────────────────────────────────────────────────────────────
// sync.jsx — SyncManager-Hooks + SyncIndicator + usePWAInstall.
// Extrahiert aus app.jsx damit flea.jsx + market.jsx diese importieren
// können ohne zirkuläre Abhängigkeiten zu app.jsx.
// ─────────────────────────────────────────────────────────────
import { useState, useEffect } from 'react';
import { T } from './theme.jsx';
import { syncManager, getPWAPrompt, clearPWAPrompt } from './offline.js';

export { syncManager };

/**
 * Hook für den globalen Sync-Status (pendingCount, syncing).
 * Subscriber wird bei Mount registriert, bei Unmount entfernt.
 */
export function useSyncStatus() {
  const [state, setState] = useState({ pendingCount: 0, syncing: false });
  useEffect(() => {
    if (!syncManager) return;
    setState({ pendingCount: syncManager.pendingCount, syncing: syncManager.syncing });
    return syncManager.subscribe((pendingCount, syncing) => setState({ pendingCount, syncing }));
  }, []);
  return state;
}

/** Zeigt ausstehende Offline-Verkäufe im Header (Orange = wartet, Grün-Spin = synct). */
export function SyncIndicator({ dark }) {
  const t = T[dark ? "dark" : "light"];
  const { pendingCount, syncing } = useSyncStatus();
  if (pendingCount === 0 && !syncing) return null;
  return (
    <div style={{
      display: "flex", alignItems: "center", gap: 4,
      padding: "4px 9px", borderRadius: 12, flexShrink: 0,
      background: syncing
        ? (dark ? "rgba(52,199,89,0.15)" : "rgba(52,199,89,0.12)")
        : (dark ? "rgba(255,159,10,0.18)" : "rgba(255,159,10,0.14)"),
      color: syncing ? "#34c759" : "#ff9f0a",
    }}>
      {syncing ? (
        <svg width="13" height="13" viewBox="0 0 13 13" fill="none"
          style={{ animation: "ptrSpin 1s linear infinite" }}>
          <path d="M6.5 1.5A5 5 0 0 1 11.5 6.5" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round"/>
          <path d="M6.5 11.5A5 5 0 0 1 1.5 6.5" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round"/>
        </svg>
      ) : (
        <svg width="13" height="13" viewBox="0 0 13 13" fill="none">
          <path d="M9.5 7.5L6.5 4.5L3.5 7.5" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"/>
          <path d="M6.5 4.5V10.5" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round"/>
          <path d="M2.5 4A3.5 3.5 0 0 1 9.5 2.5A2.5 2.5 0 0 1 11 7" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
        </svg>
      )}
      {!syncing && pendingCount > 0 && (
        <span style={{ fontSize: 11, fontWeight: 700, lineHeight: 1, letterSpacing: "-0.3px" }}>
          {pendingCount}
        </span>
      )}
    </div>
  );
}

/**
 * PWA-Install-Prompt Hook.
 * { canInstall: boolean, install: () => Promise<void> }
 */
export function usePWAInstall() {
  const isStandalone = typeof window !== "undefined" &&
    (window.matchMedia("(display-mode: standalone)").matches || window.navigator.standalone);
  const [canInstall, setCanInstall] = useState(!isStandalone && !!getPWAPrompt());

  useEffect(() => {
    if (isStandalone) return;
    const handler = () => setCanInstall(true);
    window.addEventListener("memorica:pwa_prompt_ready", handler);
    return () => window.removeEventListener("memorica:pwa_prompt_ready", handler);
  }, []);

  const install = async () => {
    const prompt = getPWAPrompt();
    if (!prompt) return;
    prompt.prompt();
    const { outcome } = await prompt.userChoice;
    if (outcome === "accepted") {
      clearPWAPrompt();
      setCanInstall(false);
    }
  };

  return { canInstall, install };
}
